import { inPeriod, useFilterParams } from '@/shared/hooks/useFilterParams';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  Server,
  Globe,
  Receipt,
  LifeBuoy,
  AlertCircle,
  Mail,
  ChevronRight,
  Clock,
  CreditCard,
  Plus,
} from 'lucide-react';
import {
  UniversalPage,
  Button,
  Card,
  StatCard,
  NotificationCard,
  CpanelButton,
  MoneyCell,
  DateCell,
  PeriodFilter,
  TABLE_PRESETS,
  useToast,
} from '@sighthost/ui';
import { useCurrentCustomer } from '@/domains/customers';
import { useServices, serviceService } from '@/domains/hosting';
import { useDomains } from '@/domains/domains-registry';
import { useInvoices, type Invoice } from '@/domains/billing';
import { useTickets } from '@/domains/support';
import { formatDateTime, relativeExpiry } from '@/shared/utils/format';
import { supabase } from '@/shared/lib/supabase';

const CPANEL_ERROR_COPY: Record<string, string> = {
  email_not_verified: 'Verify your email address to access cPanel.',
  forbidden_not_owner: "You don't have access to this hosting account.",
  rate_limited: 'Too many cPanel sessions in the past hour — please wait a few minutes.',
  whm_session_failed: 'cPanel server is unreachable right now. Please try again.',
};

/** Direct one-click pay link → public PayFast page (no extra navigation).
 *  Falls back to the in-portal invoice page if the token is somehow missing. */
function payHref(inv: Invoice): string {
  return inv.paymentToken
    ? `/pay/${inv.id}?token=${inv.paymentToken}`
    : `/app/billing/invoices/${inv.id}`;
}

function isPastDue(inv: Invoice): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(inv.dueAt) < today;
}

export default function Dashboard() {
  /**
   * AR-4 / Rule S-2 — a filter's blast radius is the container it sits in. This
   * one scopes the Activity card ONLY, so it takes its own `activity` param key
   * rather than the page-wide `period`: a future filter elsewhere on this
   * dashboard must not silently move this list, and a shared link must say
   * which card was scoped.
   */
  const activityFilters = useFilterParams({ defaultPeriod: 'all-time', paramPrefix: 'activity' });
  const activityRange = activityFilters.range;

  const { data: customer } = useCurrentCustomer();
  const { data: services, isLoading: servicesLoading } = useServices();
  const { data: domains, isLoading: domainsLoading } = useDomains();
  const { data: invoices, isLoading: invoicesLoading } = useInvoices();
  const { data: tickets, isLoading: ticketsLoading } = useTickets();
  const { toast } = useToast();
  const [resendingVerify, setResendingVerify] = useState(false);

  const launchableServices = services.filter((s) => s.status === 'active' || s.status === 'suspended');
  const activeServices = services.filter((s) => s.status === 'active').length;
  const suspendedServices = services.filter((s) => s.status === 'suspended');
  const activeDomains = domains.filter((d) => d.status === 'active' || d.status === 'expiring').length;
  const expiringDomains = domains.filter((d) => d.status === 'expiring');
  const openTickets = tickets.filter((t) => t.status !== 'closed').length;

  // Money owed — EVERY unpaid invoice (not just overdue), most-urgent first.
  const unpaidInvoices = invoices
    .filter((i) => i.status === 'unpaid' || i.status === 'overdue')
    .sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());
  const amountDueTotal = unpaidInvoices.reduce((sum, i) => sum + i.totalZAR, 0);

  const upcomingRenewals = [
    ...services
      .filter((s) => s.status === 'active' || s.status === 'suspended')
      .map((s) => ({
        kind: 'service' as const,
        title: `${s.plan.name} — ${s.primaryDomain}`,
        date: s.nextDueDate,
        amount: s.recurringAmountZAR,
        href: `/app/services/${s.id}`,
      })),
    ...domains
      .filter((d) => d.status === 'active' || d.status === 'expiring')
      .map((d) => ({
        kind: 'domain' as const,
        title: d.name,
        date: d.expiresAt,
        amount: d.priceZAR,
        href: `/app/domains/${encodeURIComponent(d.name)}`,
      })),
  ]
    .filter((item) => {
      const days = Math.round((new Date(item.date).getTime() - Date.now()) / 86_400_000);
      return days >= 0 && days <= 60;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 6);

  /**
   * D-1 (design, 2026-08-09): the period control lives in the Activity card
   * header, scoping ONLY that list. Approved four months ago and never built;
   * closed here.
   *
   * ORDER IS LOad-BEARING, and getting it wrong is an AR-5 violation in
   * miniature. The feed is `.slice(0, 5)` — a truncating window. Filtering
   * AFTER the slice would ask "which of the 5 most recent events fall in last
   * month", so choosing any past period would usually render an empty card and
   * read as "nothing happened". Filtering BEFORE the slice asks the question
   * the user meant: "the 5 most recent events IN that period". The underlying
   * invoice and ticket sets are complete for this customer (both fetched
   * unlimited), so the filtered answer is honest.
   */
  const activityAll = [
    ...invoices.map((inv) => ({
      key: `inv-${inv.id}`,
      icon: 'invoice' as const,
      label: `${inv.status === 'paid' ? 'Invoice paid' : 'Invoice issued'}: ${inv.number}`,
      date: inv.paidAt ?? inv.issuedAt,
    })),
    ...tickets.map((t) => ({
      key: `tkt-${t.id}`,
      icon: 'ticket' as const,
      label: `Ticket ${t.status === 'closed' ? 'closed' : 'updated'}: ${t.subject}`,
      date: t.lastReplyAt,
    })),
  ]
    .filter((e) => e.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const activity = activityAll
    .filter((e) => inPeriod(e.date, activityRange))
    .slice(0, 5);

  const greeting = customer ? `Welcome back, ${customer.fullName.split(' ')[0]}` : 'Welcome back';

  return (
    <UniversalPage
      title={greeting}
      description={
        customer?.lastLoginAt
          ? `Last login ${formatDateTime(customer.lastLoginAt)}${customer.lastLoginLocation ? ` from ${customer.lastLoginLocation}` : ''}.`
          : 'Overview of your services, domains, and account.'
      }
    >
      {/* Email verification — quiet nudge, above everything if unverified. */}
      {customer && !customer.emailVerified && (
        <NotificationCard
          tone="warning"
          title={<span className="inline-flex items-center gap-2"><Mail className="size-4" aria-hidden /> Verify your email address</span>}
          body={
            <div className="flex items-center justify-between gap-3">
              <span>We send invoices and notifications to your inbox — confirm your email so they don't get lost.</span>
              <Button
                size="sm"
                variant="outline"
                loading={resendingVerify}
                disabled={resendingVerify}
                onClick={() => {
                  if (resendingVerify) return;
                  setResendingVerify(true);
                  void resendVerification(toast).finally(() => setResendingVerify(false));
                }}
              >Resend verification</Button>
            </div>
          }
        />
      )}

      {/* ── TIER 1 — Money owed (loudest). One card per unpaid invoice. ── */}
      {unpaidInvoices.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-danger">Amount due</p>
            {unpaidInvoices.length > 1 && (
              <span className="text-xs text-muted-foreground">
                <MoneyCell value={amountDueTotal} /> across {unpaidInvoices.length} invoices
              </span>
            )}
          </div>
          {unpaidInvoices.slice(0, 4).map((inv) => {
            const past = isPastDue(inv);
            return (
              <Card
                key={inv.id}
                interactive={false}
                className={`border-l-4 ${past ? 'border-l-danger bg-danger/[0.03]' : 'border-l-warning bg-warning/[0.03]'}`}
              >
                <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                      <Receipt className="size-4 shrink-0" aria-hidden /> Invoice {inv.number}
                    </div>
                    <div className={`mt-0.5 text-xs ${past ? 'font-medium text-danger' : 'text-muted-foreground'}`}>
                      {past ? 'Overdue — due ' : 'Due '}
                      <DateCell value={inv.dueAt} format="long" />
                      {suspendedServices.length > 0 && past ? ' · service suspended' : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 sm:justify-end">
                    <div className="text-xl font-bold tabular-nums text-ink">
                      <MoneyCell value={inv.totalZAR} />
                    </div>
                    <Link to={payHref(inv)}>
                      <Button size="sm" variant="marketing" className="text-[11px]">
                        <CreditCard className="size-4" aria-hidden /> Pay now
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
          {unpaidInvoices.length > 4 && (
            <Link to="/app/billing" className="block text-center text-xs text-primary-ink hover:underline">
              View all {unpaidInvoices.length} unpaid invoices
            </Link>
          )}
        </section>
      )}

      {/* ── TIER 2 — Problems needing action ── */}
      {(suspendedServices.length > 0 || expiringDomains.length > 0) && (
        <section className="space-y-3">
          {suspendedServices.length > 0 && (
            <NotificationCard
              tone="danger"
              title={<span className="inline-flex items-center gap-2"><AlertCircle className="size-4" aria-hidden /> {suspendedServices.length} service{suspendedServices.length === 1 ? '' : 's'} suspended</span>}
              body={
                <div className="flex items-center justify-between gap-3">
                  <span>Your website and email on the suspended account are offline; your files are intact. Pay by card to restore it within minutes, or by EFT to have it restored once we confirm the payment (normally the next business day).</span>
                  <Link to="/app/billing"><Button size="sm" variant="outline">View invoices</Button></Link>
                </div>
              }
            />
          )}
          {expiringDomains.length > 0 && (
            <NotificationCard
              tone="warning"
              title={<span className="inline-flex items-center gap-2"><Clock className="size-4" aria-hidden /> {expiringDomains.length} domain{expiringDomains.length === 1 ? '' : 's'} expiring soon</span>}
              body={
                <div className="flex items-center justify-between gap-3">
                  <span>Renew before the registry hold to avoid losing your domain.</span>
                  <Link to="/app/domains"><Button size="sm" variant="outline">Review domains</Button></Link>
                </div>
              }
            />
          )}
        </section>
      )}

      {/* ── TIER 3 — Healthy: cPanel launcher (compact) ── */}
      {launchableServices.length > 0 && (
        <Card tone="hairline" interactive={false}>
          <header className="flex items-center justify-between border-b border-border px-6 py-3">
            <h2 className="font-display text-sm font-semibold text-ink">Open your hosting</h2>
            <span className="hidden text-xs text-muted-foreground sm:inline">
              Build, install WordPress, manage email — 400+ tools inside cPanel
            </span>
          </header>
          <ul className="divide-y divide-border">
            {launchableServices.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-6 py-3">
                <span className={`size-2 shrink-0 rounded-full ${s.status === 'active' ? 'bg-success' : 'bg-danger'}`} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-ink">{s.primaryDomain}</div>
                  <div className="text-xs text-muted-foreground">{s.plan.name}{s.status === 'suspended' ? ' · suspended' : ''}</div>
                </div>
                <CpanelButton
                  service={{ cpanel_username: s.cpanelUsername, status: s.status }}
                  onIssueSession={(cpanel_username) => serviceService.createCpanelSession({ cpanel_username })}
                  onError={(err) => {
                    toast({ tone: 'warning', title: 'Could not open cPanel', body: CPANEL_ERROR_COPY[err.code] ?? err.message });
                  }}
                  variant="primary"
                  size="sm"
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Overview stats */}
      <section>
        <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <span className="text-primary">01/</span> Overview
        </p>
        <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
          <Link to="/app/services" className="block">
            <StatCard
              label="Active services"
              value={servicesLoading ? '…' : String(activeServices)}
              sub={services.length === activeServices ? 'all healthy' : `${services.length - activeServices} need attention`}
            />
          </Link>
          <Link to="/app/domains" className="block">
            <StatCard
              label="Domains"
              value={domainsLoading ? '…' : String(activeDomains)}
              sub={expiringDomains.length > 0 ? `${expiringDomains.length} expiring soon` : 'all current'}
            />
          </Link>
          <Link to="/app/billing" className="block">
            <StatCard
              label="Amount due"
              value={invoicesLoading ? '…' : unpaidInvoices.length === 0 ? 'R0' : <MoneyCell value={amountDueTotal} />}
              sub={unpaidInvoices.length === 0 ? 'nothing owing' : `${unpaidInvoices.length} unpaid invoice${unpaidInvoices.length === 1 ? '' : 's'}`}
            />
          </Link>
          <Link to="/app/support" className="block">
            <StatCard
              label="Open tickets"
              value={ticketsLoading ? '…' : String(openTickets)}
              sub={openTickets === 0 ? 'no support requests' : 'awaiting reply'}
            />
          </Link>
        </div>
      </section>

      {/* Quick actions */}
      <section className="flex flex-wrap gap-3">
        <Link to="/app/services/new"><Button variant="outline" size="sm"><Plus className="size-4" /> Order hosting</Button></Link>
        <Link to="/app/domains/register"><Button variant="outline" size="sm"><Globe className="size-4" /> Register a domain</Button></Link>
        <Link to="/app/support/new"><Button variant="outline" size="sm"><LifeBuoy className="size-4" /> New support ticket</Button></Link>
      </section>

      {/* Renewals + Activity */}
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card interactive={false}>
          <header className="flex items-center justify-between border-b border-border px-6 py-3">
            <h2 className="text-sm font-semibold text-ink">Upcoming renewals</h2>
            <span className="text-xs text-muted-foreground">Next 60 days</span>
          </header>
          {upcomingRenewals.length === 0 ? (
            <p className="px-6 py-8 text-center text-sm text-muted-foreground">Nothing renewing in the next 60 days.</p>
          ) : (
            <ul className="divide-y divide-border">
              {upcomingRenewals.map((item) => (
                <li key={item.href}>
                  <Link to={item.href} className="flex items-center gap-3 px-6 py-3 transition-colors hover:bg-primary/5">
                    {item.kind === 'service' ? <Server className="h-4 w-4 shrink-0 text-muted-foreground" /> : <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-ink">{item.title}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <DateCell value={item.date} format="long" />
                        <span>· {relativeExpiry(item.date)}</span>
                      </div>
                    </div>
                    <div className="text-right"><MoneyCell value={item.amount} /></div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card interactive={false}>
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-6 py-3">
            <h2 className="text-sm font-semibold text-ink">Recent activity</h2>
            {/*
              F-3: rendered only when there IS activity to scope. A period
              control above an empty card implies a filter the screen does not
              really offer, and would make "no activity yet" look like a filter
              result rather than a fact.
            */}
            {activityAll.length > 0 ? (
              <PeriodFilter
                value={activityFilters.period}
                from={activityFilters.from}
                to={activityFilters.to}
                onChange={activityFilters.setPeriod}
                presets={TABLE_PRESETS}
                variant="menu"
                label="Activity period"
              />
            ) : (
              <span className="text-xs text-muted-foreground">Latest {activity.length || ''}</span>
            )}
          </header>
          <ul className="divide-y divide-border">
            {activity.map((e) => (
              <li key={e.key} className="flex items-center gap-3 px-6 py-3 text-sm">
                {e.icon === 'invoice' ? <Receipt className="h-4 w-4 shrink-0 text-muted-foreground" /> : <LifeBuoy className="h-4 w-4 shrink-0 text-muted-foreground" />}
                <span className="flex-1 truncate text-foreground-soft">{e.label}</span>
                <DateCell value={e.date} format="short" />
              </li>
            ))}
            {activity.length === 0 && (
              <li className="px-6 py-8 text-center text-sm text-muted-foreground">
                {activityAll.length === 0
                  ? 'No activity yet.'
                  : 'Nothing in this period — widen it, or choose All time.'}
              </li>
            )}
          </ul>
        </Card>
      </section>
    </UniversalPage>
  );
}

async function resendVerification(toast: ReturnType<typeof useToast>['toast']): Promise<void> {
  // Resolve email at click-time from auth.users (authoritative), matching
  // SettingsSecurityPage — customer_profile can drift after an email change.
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser?.email) return;
  const { error } = await supabase.auth.resend({ type: 'signup', email: authUser.email });
  if (error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('already confirmed') || msg.includes('already been confirmed')) {
      toast({ tone: 'success', title: 'Email already verified', body: 'Refreshing your dashboard.' });
      window.location.reload();
      return;
    }
    toast({ tone: 'danger', title: 'Could not send', body: error.message });
    return;
  }
  toast({ tone: 'success', title: 'Verification email sent', body: `Check ${authUser.email}.` });
}
